import React from 'react';
import { X, User, Mail, Phone, Calendar, DollarSign, Star, MapPin } from 'lucide-react';

const ClientProfileModal = ({ isOpen, onClose, client }) => {
    if (!isOpen || !client) return null;

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
                {/* Header */}
                <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-blue-600 p-6 flex justify-between items-center rounded-t-2xl">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                            <User className="w-8 h-8 text-white" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-white">{client.name}</h2>
                            <p className="text-white/80 text-sm">Profil client</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white/80 hover:text-white transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Contact Info */}
                    <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
                        <h3 className="text-lg font-semibold text-white mb-4">Informations de contact</h3>
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <Mail className="w-5 h-5 text-blue-400" />
                                <div>
                                    <p className="text-sm text-gray-400">Email</p>
                                    <p className="text-white font-medium">{client.email || 'N/A'}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Phone className="w-5 h-5 text-green-400" />
                                <div>
                                    <p className="text-sm text-gray-400">Téléphone</p>
                                    <p className="text-white font-medium">{client.phone || 'N/A'}</p>
                                </div>
                            </div>
                            {client.city && (
                                <div className="flex items-center gap-3">
                                    <MapPin className="w-5 h-5 text-red-400" />
                                    <div>
                                        <p className="text-sm text-gray-400">Ville</p>
                                        <p className="text-white font-medium">{client.city}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-gradient-to-br from-blue-600/20 to-blue-700/20 border border-blue-500/30 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Calendar className="w-5 h-5 text-blue-400" />
                                <p className="text-sm text-blue-300">Réservations</p>
                            </div>
                            <p className="text-2xl font-bold text-white">{client.total_reservations || 0}</p>
                        </div>

                        <div className="bg-gradient-to-br from-green-600/20 to-green-700/20 border border-green-500/30 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <DollarSign className="w-5 h-5 text-green-400" />
                                <p className="text-sm text-green-300">Revenu total</p>
                            </div>
                            <p className="text-2xl font-bold text-white">
                                {(client.total_revenue || 0).toLocaleString()}
                            </p>
                            <p className="text-xs text-green-300/70">DZD</p>
                        </div>

                        <div className="bg-gradient-to-br from-yellow-600/20 to-yellow-700/20 border border-yellow-500/30 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Star className="w-5 h-5 text-yellow-400" />
                                <p className="text-sm text-yellow-300">Note moyenne</p>
                            </div>
                            <p className="text-2xl font-bold text-white">{client.avg_rating || 'N/A'}</p>
                        </div>

                        <div className="bg-gradient-to-br from-purple-600/20 to-purple-700/20 border border-purple-500/30 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <DollarSign className="w-5 h-5 text-purple-400" />
                                <p className="text-sm text-purple-300">Revenu moyen</p>
                            </div>
                            <p className="text-2xl font-bold text-white">
                                {client.total_reservations > 0
                                    ? Math.round(client.total_revenue / client.total_reservations).toLocaleString()
                                    : '0'}
                            </p>
                            <p className="text-xs text-purple-300/70">DZD</p>
                        </div>
                    </div>

                    {/* Activity */}
                    <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
                        <h3 className="text-lg font-semibold text-white mb-4">Activité</h3>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <p className="text-gray-400">Dernière réservation</p>
                                <p className="text-white font-medium">{formatDate(client.last_reservation)}</p>
                            </div>
                            {client.joined && (
                                <div className="flex justify-between items-center">
                                    <p className="text-gray-400">Client depuis</p>
                                    <p className="text-white font-medium">{formatDate(client.joined)}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                        <button
                            onClick={() => window.location.href = `mailto:${client.email}`}
                            className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium flex items-center justify-center gap-2"
                        >
                            <Mail className="w-4 h-4" />
                            Envoyer un email
                        </button>
                        <button
                            onClick={onClose}
                            className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
                        >
                            Fermer
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientProfileModal;
